/* "Open in surface →" lands here: the row's KEPT pane, hosted inside
 * the workbench (W6). Row actions route into the EXISTING panes until
 * the per-surface design passes replace them — no pane is rewritten by
 * this wave. */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import TaskPane from '../TaskPane'
import { isTypingTarget } from '../keyboard/useListKeys'
import { useRkDecksEnabled } from '../rkdecks/api'
import { SURFACE_NAMES, SURFACE_SLUGS, surfaceFor, surfaceForSlug } from './placement'
import { ErrorState, Skeleton } from '../state'
import { useRead } from '../navigation/useRead'
import ContextWorkbenchRoute from './ContextWorkbenchRoute'
import './workbench.css'

export default function TaskPaneHost() {
  const { surface } = useParams()
  return surface === 'other-ops' ? <OtherTaskRoute /> : <LegacyTaskPaneHost />
}

function OtherTaskRoute() {
  const [refresh, setRefresh] = useState(0)
  const read = useRead<{ enabled: boolean }>('/api/navigation/capabilities', refresh)
  if (read.loading) return <Skeleton kind="cards" n={1} height={260} />
  if (read.error || typeof read.value?.enabled !== 'boolean')
    return (
      <ErrorState
        what="Workbench context"
        error={read.error ?? new Error('Context availability could not be read.')}
        onRetry={() => setRefresh((value) => value + 1)}
      />
    )
  return read.value.enabled ? <ContextWorkbenchRoute /> : <LegacyTaskPaneHost />
}

function LegacyTaskPaneHost() {
  const { surface: slug, taskId } = useParams()
  const navigate = useNavigate()
  const rkDecksOn = useRkDecksEnabled()
  const [task, setTask] = useState<TaskQueueItem | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!taskId) return
    let cancelled = false
    apiFetch<TaskQueueItem>(`/api/tasks/${taskId}`)
      .then((t) => {
        if (!cancelled) setTask(t)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err)
      })
    return () => {
      cancelled = true
    }
  }, [taskId, tick])
  const retry = () => {
    setError(null)
    setTick((t) => t + 1)
  }

  const surface = surfaceForSlug(slug)
  const taskSurface = task ? surfaceFor(task.task_type) : null
  const backSlug = taskSurface ? SURFACE_SLUGS[taskSurface] : slug

  /* Back to the queue WITH the row re-selected (keyboard canon: Esc
   * returns focus to the row) — the ?task= deep link preselects it. */
  const back = useCallback(
    () =>
      navigate(`/v2/workbench/${backSlug}${taskId ? `?task=${encodeURIComponent(taskId)}` : ''}`),
    [navigate, backSlug, taskId]
  )
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      // Innermost first: an Esc typed in a reason box leaves the box; the
      // next one leaves the page. Popovers spend their own Esc upstream.
      if (isTypingTarget(e.target)) {
        ;(e.target as HTMLElement).blur()
        return
      }
      back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [back])

  return (
    <main className="wb" data-testid="wb-task-pane-host">
      <div className="inner">
        <div className="streamhead">
          <button type="button" className="backlink" onClick={back}>
            ← {surface ? SURFACE_NAMES[surface] : 'Workbench'}
          </button>
        </div>
        {error ? <ErrorState what="task" error={error} onRetry={retry} /> : null}
        {task ? (
          <TaskPane
            task={task}
            rkDecksOn={rkDecksOn}
            onResolved={() => navigate(`/v2/workbench/${backSlug}`)}
          />
        ) : error ? null : (
          <Skeleton kind="cards" n={1} height={260} />
        )}
      </div>
    </main>
  )
}
